import { useState, useEffect } from "react";
import { apiFetch } from "../../lib/config";

interface TraceListItem {
  trace_id: string;
  service_name: string;
  started_at: string;
  span_count: number;
}

interface Span {
  span_id: string;
  parent_span_id: string;
  operation_name: string;
  started_at: string;
  duration_ms: number;
  status: string;
  attributes: Record<string, unknown>;
}

interface TraceDetail {
  trace_id: string;
  service_name: string;
  started_at: string;
  spans: Span[];
}

function TraceWaterfall({ trace }: { trace: TraceDetail }) {
  if (!trace.spans.length) return (
    <div className="ac-empty"><div className="ac-empty__title">No spans in this trace.</div></div>
  );

  const maxDuration = trace.spans.reduce((m, s) => Math.max(m, s.duration_ms), 0) || 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }} aria-label={`Trace waterfall for ${trace.trace_id}`}>
      {trace.spans.map((span) => {
        const width = Math.max(2, (span.duration_ms / maxDuration) * 100);
        return (
          <div key={span.span_id} style={{ display: "grid", gridTemplateColumns: "200px 1fr 60px", alignItems: "center", gap: "0.5rem", fontSize: "0.8125rem" }}>
            <span style={{ color: "var(--aptean-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={span.operation_name}>{span.operation_name}</span>
            <div style={{ height: "12px", background: "var(--aptean-border-default)", borderRadius: "2px", overflow: "hidden" }}>
              <div
                style={{
                  width: `${width}%`,
                  height: "100%",
                  borderRadius: "2px",
                  background: span.status === "error" ? "#a4262c" : "var(--aptean-accent)",
                }}
                title={`${span.duration_ms}ms`}
              />
            </div>
            <span style={{ color: "var(--aptean-text-secondary)", textAlign: "right" }}>{span.duration_ms}ms</span>
          </div>
        );
      })}
    </div>
  );
}

export function TracesPage() {
  const [traces, setTraces] = useState<TraceListItem[]>([]);
  const [selected, setSelected] = useState<TraceDetail | null>(null);
  const [loading, setLoading] = useState(false);

  async function loadTraces() {
    setLoading(true);
    const r = await apiFetch("/api/v1/telemetry/traces/");
    if (r.ok) setTraces(await r.json());
    setLoading(false);
  }

  async function loadTrace(traceId: string) {
    const r = await apiFetch(`/api/v1/telemetry/traces/${traceId}/`);
    if (r.ok) setSelected(await r.json());
  }

  useEffect(() => { loadTraces(); }, []);

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Distributed Traces</h1>
          <p className="ac-page-subtitle">Inspect request traces and span waterfalls across services</p>
        </div>
        <div className="ac-page-header__actions">
          <button type="button" className="aptean-btn aptean-btn--secondary" onClick={loadTraces} disabled={loading}>Refresh</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: selected ? "1fr 1fr" : "1fr", gap: "1.25rem" }}>
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Started</th>
                <th>Spans</th>
                <th className="ac-th--right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4}><div className="ac-empty"><div className="ac-empty__body">Loading traces...</div></div></td></tr>
              ) : traces.length === 0 ? (
                <tr><td colSpan={4}><div className="ac-empty"><div className="ac-empty__title">No traces found</div></div></td></tr>
              ) : traces.map((t) => (
                <tr key={t.trace_id} style={selected?.trace_id === t.trace_id ? { background: "var(--aptean-accent-subtle)" } : {}}>
                  <td className="ac-row-title">{t.service_name}</td>
                  <td className="ac-td--muted">{new Date(t.started_at).toLocaleString()}</td>
                  <td className="ac-td--muted">{t.span_count}</td>
                  <td className="ac-td--right">
                    <button
                      type="button"
                      className="aptean-btn aptean-btn--ghost"
                      style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                      onClick={() => loadTrace(t.trace_id)}
                    >
                      View Waterfall
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selected && (
          <div className="ac-table-wrap" style={{ padding: "1.25rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <div>
                <div className="ac-row-title">Trace: <code style={{ fontFamily: "var(--aptean-font-mono)", fontSize: "0.8125rem" }}>{selected.trace_id}</code></div>
                <div className="ac-row-sub">{selected.service_name} &mdash; {selected.spans.length} spans</div>
              </div>
              <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem" }} onClick={() => setSelected(null)}>Close</button>
            </div>
            <TraceWaterfall trace={selected} />
          </div>
        )}
      </div>
    </div>
  );
}
